/**
 * @copyright Copyright 2023-2025 Epic Systems Corporation
 * @file button to update the token validity
 * @module Epic.AppOrchard.Showroom.ShowroomUpdateTokensButton
 */

import { Link, useToast } from "@chakra-ui/react";
import { ISuccessHandler, useAsync } from "@epic/react-async-hook";
import { config } from "ao/appConfig";
import { StatusModal } from "ao/components/Core";
import { requestDownload } from "ao/components/Showroom/data/data";
import { DownloadApprovedStatus } from "ao/data";
import { IUserSecurity } from "ao/types";
import { IShowroomAppDownloadModel } from "ao/types/showroom";
import { isInFlight } from "ao/utils/useAsyncHelpers";
import React, { FunctionComponent, memo, useCallback, useRef, useState } from "react";
import { EditTokensModal, setDefaultTokens } from "./EditTokensModal";

interface IProps {
	appDetails: IShowroomAppDownloadModel;
	userSecurity: IUserSecurity;
	setAppDetails: (x: IShowroomAppDownloadModel) => void;
}

export const ShowroomUpdateTokensButton: FunctionComponent<IProps> = memo((props) => {
	const { appDetails, setAppDetails } = props;

	//#region local state and hooks
	const [showTokenModal, setShowTokenModal] = useState<boolean>(false);
	const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);

	const appModel = appDetails.appModel;
	const updateTokenButtonRef = useRef<HTMLAnchorElement>(null);

	const toast = useToast();

	//#endregion

	//#region async API handlers

	const handleRequestDownloadSuccess = useCallback<ISuccessHandler<typeof requestDownload>>(
		(response, extras) => {
			const [, , , accessTokenValidity, refreshTokenValidity, disableIndefiniteAccess] = extras.params;

			if (response.Success) {
				setShowTokenModal(false);
				setShowSuccessModal(true);

				const newDetails = {
					...appDetails,
					accessTokenValidity: accessTokenValidity ?? appDetails.accessTokenValidity,
					refreshTokenValidity: refreshTokenValidity ?? appDetails.refreshTokenValidity,
					disableIndefiniteAccess: disableIndefiniteAccess ?? appDetails.disableIndefiniteAccess,
				};

				setAppDetails(setDefaultTokens(newDetails));
			} else {
				toast({
					title: "Error",
					status: "error",
					description: response.Message,
					duration: config.ToastDuration,
					isClosable: true,
				});
			}
		},
		[appDetails, setAppDetails, toast],
	);

	const [requestingDownloadState, requestAppDownload] = useAsync(requestDownload, {
		executeImmediately: false,
		displayName: "requestDownload",
		onSuccess: (response, extras) => handleRequestDownloadSuccess(response, extras),
		onFailure: () =>
			toast({
				title: "Error",
				description:
					"Please try again later or contact your Epic representative if this continues to happen.",
				status: "error",
				duration: config.ToastDuration,
				isClosable: true,
			}),
	});

	const handleUpdateClicked = useCallback(() => {
		setShowTokenModal(true);
	}, []);

	const handleUpdate = useCallback(() => {
		requestAppDownload(
			appDetails.appId!,
			appDetails.participantTermsId,
			appModel.agreementId,
			appDetails.accessTokenValidity,
			appDetails.refreshTokenValidity,
			appDetails.disableIndefiniteAccess,
		);
	}, [
		appDetails.accessTokenValidity,
		appDetails.appId,
		appModel.agreementId,
		appDetails.disableIndefiniteAccess,
		appDetails.participantTermsId,
		appDetails.refreshTokenValidity,
		requestAppDownload,
	]);

	const isRequesting = isInFlight(requestingDownloadState);

	//#endregion

	return (
		<>
			<Link
				onClick={handleUpdateClicked}
				isLoading={isRequesting}
				display="inline-block"
				alignContent="center"
				loadingText="Updating Token Validity"
				ref={updateTokenButtonRef}
				disabled={
					appDetails.vendorApproved === DownloadApprovedStatus.Approved && appDetails.isSyncDisabled
				}
				title={`Update how long the product's OAuth 2.0 tokens are valid. ${
					appDetails.vendorApproved === DownloadApprovedStatus.Approved && appDetails.isSyncDisabled
						? "Syncing to your Production system has been temporarily disabled for this app by Epic while the app is modified so you cannot modify the token validity. Contact your Epic representative and mention SLG 9084550 if you have any questions."
						: ""
				}`}
			>
				Update Token Validity
			</Link>

			<EditTokensModal
				isOpen={showTokenModal}
				onClose={() => setShowTokenModal(false)}
				appDetails={appDetails}
				setAppDetails={setAppDetails}
				onAccept={handleUpdate}
			/>
			<StatusModal
				title="Success"
				message={
					appModel.hasIndefiniteAccess
						? "Successfully updated the token validity duration and/or whether the product has indefinite access. The settings will take effect when new tokens are issued to the product."
						: "Successfully updated the token validity duration. The settings will take effect when new tokens are issued to the product."
				}
				isOpen={showSuccessModal}
				onClose={() => setShowSuccessModal(false)}
				status="success"
				closeButtonText="Okay"
				focusOnCloseRef={updateTokenButtonRef}
			/>
		</>
	);
});
